/**
 * Consimtamantul, ca material de contract.
 *
 * Doua consimtaminte diferite traiesc aici, si diferenta dintre ele e intreg
 * subiectul:
 *
 *  1. **Consimtamantul de urmarire** (bannerul) — priveste ce se incarca in
 *     browserul vizitatorului: masuratoare (GA4) si marketing (pixel Meta).
 *     Il da vizitatorul PLATFORMEI, inainte sa comande orice.
 *
 *  2. **Consimtamantul de marketing catre vanzator** (la finalizare) — priveste
 *     dreptul unui restaurant anume de a-i trimite oferte dupa comanda. Il da
 *     clientul acelui RESTAURANT, nu platformei.
 *
 * Al doilea e cel care lipsea. Pana acum exista o singura caseta — „Vreau sa
 * primesc oferte pe e-mail. (optional)" — data platformei, care nu numea pe
 * nimeni, si care se stoca drept boolean fara nicio dovada. Restaurantele
 * primeau contactele clientului cu fiecare comanda si niciun semnal despre ce
 * au voie sa faca cu ele.
 *
 * Un consimtamant care nu numeste destinatarul nu e valabil pentru el. De aceea
 * `SellerMarketingConsent` poarta `vendorId` si numele afisat, iar textul exact
 * pus in fata clientului se salveaza odata cu decizia.
 */
export const CONSENT_PURPOSES = ["analytics", "marketing"];
/**
 * Versiunea implicita a textelor de consimtamant.
 *
 * Cand textul se schimba in fond (alte scopuri, alti destinatari), versiunea se
 * schimba si ea, iar deciziile luate pe textul vechi nu mai sunt valabile — se
 * intreaba din nou. O piata care isi personalizeaza textele isi pune propria
 * versiune in `config.consent.textVersion`; altfel se foloseste asta.
 *
 * NU o schimba pentru corecturi de forma (o virgula, un cuvant mai clar): ai
 * reintreba toata baza de clienti degeaba, iar oamenii care vad al doilea banner
 * fara motiv apasa „refuz".
 */
export const CONSENT_TEXT_VERSION_DEFAULT = "2026-08-16";
/**
 * Starea „inca nu a decis". Distincta de „a refuzat tot": prima arata bannerul,
 * a doua nu. Confuzia dintre ele e exact bug-ul din POS, unde vizitatorii din
 * afara UE ramaneau pe `false` implicit si nu vedeau niciodata bannerul — deci
 * nu puteau nici accepta, nici refuza, iar tot tracking-ul era mort.
 */
export const CONSENT_UNDECIDED = null;
export function consentAllows(state, purpose) {
    if (state === null)
        return false;
    return purpose === "analytics" ? state.analytics : state.marketing;
}
/**
 * Decizia mai e valabila pe textul curent?
 *
 * Se compara versiunea, nu continutul: textul se poate schimba in forma fara sa
 * schimbe ce a inteles omul.
 */
export function consentIsCurrent(state, textVersion) {
    return state !== null && state.textVersion === textVersion;
}
/** Textele implicite. O piata le poate inlocui din `config.consent`. */
export const CONSENT_TEXTS_RO = {
    bannerTitle: "Cum folosim datele",
    bannerBody: "Folosim cookie-uri necesare ca site-ul să funcționeze. Cu acordul tău folosim și " +
        "cookie-uri de măsurare (câți vizitatori avem și ce pagini se văd) și de marketing " +
        "(ca reclamele pe care le vezi în altă parte să fie relevante). Poți schimba alegerea " +
        "oricând din subsolul paginii.",
    analyticsLabel: "Măsurare",
    analyticsHint: "Câți vizitatori avem și ce pagini se văd. Nu te identifică personal.",
    marketingLabel: "Marketing",
    marketingHint: "Ca reclamele pe care le vezi în altă parte să fie relevante.",
    acceptAll: "Accept toate",
    rejectAll: "Doar cele necesare",
    save: "Salvează alegerea",
    manage: "Setări",
    /** `{seller}` se inlocuieste cu numele afisat al vanzatorului. */
    sellerMarketing: "Vreau să primesc oferte și noutăți de la {seller}, pe e-mail.",
    sellerMarketingHint: "Doar de la acest restaurant. Te poți dezabona oricând din orice mesaj primit.",
};
/** Textul bifei de la finalizare, cu numele vanzatorului pus la locul lui. */
export function sellerMarketingText(template, sellerName) {
    return template.replace(/\{seller\}/g, sellerName);
}
/** Ordine stabila: aceeasi lista trebuie sa dea aceeasi versiune, mereu. */
function sortedTaxIds(recipients) {
    return [...new Set(recipients.map((r) => r.taxId.trim().toUpperCase()).filter((t) => t.length > 0))].sort();
}
/**
 * Amprenta listei de destinatari. FNV-1a pe 32 de biti — deterministă, scurta, si
 * disponibila si in browser fara dependinte.
 *
 * Nu e o garantie criptografica si nici nu trebuie sa fie: nu apara un secret, ci
 * raspunde la o singura intrebare — „s-a schimbat lista fata de cea pe care omul
 * a citit-o?".
 */
function amprentaDestinatari(taxIds) {
    let hash = 0x811c9dc5;
    for (const value of taxIds.join("|")) {
        hash ^= value.codePointAt(0) ?? 0;
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, "0");
}
/**
 * Versiunea EFECTIVA a consimtamantului, pentru lista data de destinatari.
 *
 * Capcana pe care o inchide: `textVersion` e un sir scris de om, iar
 * `consentIsCurrent` compara doar versiuni. Daca in grup intra o firma noua si
 * nimeni nu bumpeaza manual versiunea, toate consimtamintele vechi raman
 * „curente" pentru o firma pe care nimeni nu a numit-o vreodata — exact
 * incalcarea pe care `SellerMarketingConsent` a fost construit sa o previna.
 *
 * Legand versiunea de amprenta listei, ea se schimba EXACT cand se schimba
 * destinatarii, si numai atunci. Tiparul e acelasi cu `weightsVersion`, care leaga
 * un numar de amprenta ponderilor efective.
 *
 * Cu un singur destinatar versiunea ramane neatinsa: nu e nimic de enumerat peste
 * vanzatorul pe care bifa il numeste oricum, deci nu se reintreaba nimeni degeaba.
 */
export function consentVersionForRecipients(baseVersion, recipients) {
    const taxIds = sortedTaxIds(recipients);
    if (taxIds.length <= 1)
        return baseVersion;
    return `${baseVersion}~${amprentaDestinatari(taxIds)}`;
}
/**
 * Cum se scriu destinatarii in fata omului: „Firma A (CUI 123), Firma B (CUI 456)".
 *
 * CUI-ul nu e decor. Doua firme pot avea denumiri asemanatoare, iar peste un an
 * intrebarea „cui i-am dat datele" trebuie sa aiba un raspuns care identifica
 * persoana juridica, nu unul care seamana cu ea.
 */
export function formatRecipients(recipients) {
    return recipients.map((r) => `${r.legalName} (CUI ${r.taxId})`).join(", ");
}
/**
 * Textul bifei cand piata e un grup de firme.
 *
 * Vanzatorul ramane numit primul — el e cel care gateste si cel pe care omul il
 * vede. Restul firmelor se enumera explicit, fiindca fiecare e operator
 * independent (ADR-015) si niciuna nu primeste nimic pe seama apartenentei la
 * grup: doar pe seama acestei enumerari, citite si acceptate.
 *
 * Aceeasi functie compune textul si in vitrina, si pe server. Doua compuneri
 * diferite ar produce doua texte diferite, iar dovada salvata ar fi alta decat
 * cea citita — exact ce face o dovada inutilizabila.
 */
export function groupMarketingText(template, seller, recipients) {
    const baza = sellerMarketingText(template, seller.displayName);
    // Filtrarea e pe CUI, nu pe nume. `displayName` („Cafeneaua Zori") si
    // `legalName` („ZORI GASTRO SRL") sunt coloane diferite si aproape niciodata
    // egale: o comparatie de nume nu ar elimina niciodata vanzatorul, iar textul ar
    // spune „celelalte firme ale grupului" enumerand chiar firma vanzatorului.
    const cuiVanzator = seller.taxId.trim().toUpperCase();
    const altele = recipients.filter((r) => r.taxId.trim().toUpperCase() !== cuiVanzator);
    if (altele.length === 0)
        return baza;
    return `${baza} Datele pot fi folosite in acest scop si de celelalte firme ale grupului: ${formatRecipients(altele)}.`;
}
/**
 * Sablonul bifei, rezolvat identic in vitrina si pe server.
 *
 * Cascada nu e cosmetica: vitrina folosea `config.consent.sellerMarketingText`
 * apoi traducerile (`texts[locale]`, `texts[defaultLocale]`), iar serverul se uita
 * DOAR la prima. Pe o piata care si-a tradus bifa, omul citea un text si se salva
 * altul ca dovada — exact ce face o dovada inutilizabila. O singura functie, doi
 * apelanti: divergenta nu mai are unde sa apara.
 */
export function resolveSellerMarketingTemplate(input) {
    if (typeof input.consentText === "string" && input.consentText.length > 0)
        return input.consentText;
    const cheie = "consent.seller_marketing";
    const inLocale = input.locale === undefined ? undefined : input.texts?.[input.locale]?.[cheie];
    if (typeof inLocale === "string" && inLocale.length > 0)
        return inLocale;
    const inDefault = input.defaultLocale === undefined ? undefined : input.texts?.[input.defaultLocale]?.[cheie];
    if (typeof inDefault === "string" && inDefault.length > 0)
        return inDefault;
    return CONSENT_TEXTS_RO.sellerMarketing;
}
/**
 * Are voie contactul COMPLET al clientului sa plece la restaurant?
 *
 * Numele si telefonul pleaca mereu — fara ele restaurantul nu poate livra, iar
 * temeiul lor e executarea comenzii. Emailul e altceva: e materia prima a
 * marketingului, si nu are ce cauta la o firma pe care clientul nu a ales-o si
 * despre care nu i s-a spus nimic.
 *
 * Doua situatii il justifica, si numai ele:
 *  - piata are o singura firma — „restaurantul" si „piata" sunt acelasi operator;
 *  - piata e un grup declarat — firmele au fost ENUMERATE in fata omului, iar el
 *    a acceptat lista.
 *
 * Pe o piata cu mai multe firme independente, raspunsul e nu.
 */
export function contactSharingIsFull(input) {
    // „Nu putem enumera" se trateaza inaintea oricarui alt criteriu, inclusiv
    // inaintea grupului declarat. Altfel piata cea mai fragmentata — cea pentru
    // care regula a fost scrisa — primea tratamentul cel mai permisiv: rosterul
    // taiat iesea gol, `0 <= 1` era adevarat, si emailul pleca la toata lumea.
    // Iar pe o piata-grup peste plafon, vitrina arata bifa mono-vanzator in timp
    // ce datele plecau pe temei de grup: textul si datele spuneau lucruri diferite.
    if (input.truncated)
        return false;
    if (input.recipientScope === "market_group")
        return true;
    return input.firmCount <= 1;
}
//# sourceMappingURL=consent.js.map